import { FC, ChangeEvent } from 'react'
import { TextField, InputAdornment } from '@mui/material'
import type { TextFieldProps } from '@mui/material'

/**
 * Campo de entrada de moeda (R$) com máscara ao digitar.
 *
 * Mantém sempre o formato pt-BR com separador de milhar e duas casas decimais
 * (ex.: digitar "1234567" exibe "12.345,67"). O valor exposto via `onChange`
 * é sempre um `number` em reais — pronto para enviar ao backend.
 */
export type CurrencyFieldProps = Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
  value: number | null | undefined
  onChange: (value: number) => void
}

const nf = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

export const CurrencyField: FC<CurrencyFieldProps> = ({
  value,
  onChange,
  InputProps,
  ...rest
}) => {
  const numeric = typeof value === 'number' && Number.isFinite(value) ? value : 0
  const display = nf.format(numeric)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/\D/g, '')
    const cents = digits ? parseInt(digits, 10) : 0
    onChange(cents / 100)
  }

  return (
    <TextField
      {...rest}
      value={display}
      onChange={handleChange}
      inputProps={{ inputMode: 'numeric', ...rest.inputProps }}
      InputProps={{
        startAdornment: <InputAdornment position="start">R$</InputAdornment>,
        ...InputProps,
      }}
    />
  )
}
